import { Card, Flex, Form, Table } from 'antd'

import {
  ClinicalAnnualUpdate,
  HivAnamnesisCaseResponse,
  HivAnamnesisFormSection
} from 'src/api/requests'
import { formatUtcDateTime } from 'src/libs'
import { useLocales } from 'src/locales'

import { SystemRefreshButton } from '../SystemRefreshButton'

import { ClinicalFields } from './ClinicalFields'

type Props = {
  hivAnamnesisCase?: HivAnamnesisCaseResponse
}

/**
 * F. Clinical data (fields 51–60.1) with the history of the yearly ECDC updates (N2); the update itself is
 * recorded from the page header.
 */
export const ClinicalCard = ({ hivAnamnesisCase }: Props) => {
  const { t } = useLocales()
  const form = Form.useFormInstance()
  const diagnosisIcd10Id = Form.useWatch(
    ['form', 'testing', 'diagnosis', 'icd10Id'],
    form
  )

  const annualUpdates = hivAnamnesisCase?.form?.annualUpdates ?? []

  return (
    <Card
      title={t('hiv_anamnesis.clinical.title')}
      extra={
        <SystemRefreshButton sections={[HivAnamnesisFormSection.CLINICAL]} />
      }
    >
      <Flex vertical gap={24}>
        <ClinicalFields
          prefix={['form', 'clinical']}
          diagnosisIcd10Id={diagnosisIcd10Id}
        />

        {annualUpdates.length > 0 && (
          <Table<ClinicalAnnualUpdate>
            rowKey="year"
            size="small"
            pagination={false}
            title={() => t('hiv_anamnesis.annual_update.history')}
            dataSource={[...annualUpdates].reverse()}
            columns={[
              {
                key: 'year',
                title: t('hiv_anamnesis.annual_update.year'),
                dataIndex: 'year'
              },
              {
                key: 'recordedAt',
                title: t('hiv_anamnesis.annual_update.recorded_at'),
                render: (_, record) => formatUtcDateTime(record.recordedAt)
              },
              {
                key: 'arv',
                title: t('hiv_anamnesis.clinical.arv_treatment'),
                render: (_, record) =>
                  record.clinical?.arvTreatment
                    ? t(
                        `hiv_anamnesis.tri_state.${record.clinical.arvTreatment}`
                      )
                    : '-'
              },
              {
                key: 'aids',
                title: t('hiv_anamnesis.clinical.aids_stage'),
                render: (_, record) =>
                  record.clinical?.aidsStage
                    ? t(`hiv_anamnesis.tri_state.${record.clinical.aidsStage}`)
                    : '-'
              },
              {
                key: 'cd4',
                title: t('hiv_anamnesis.clinical.cd4_latest'),
                render: (_, record) => record.clinical?.latestCd4?.count ?? '-'
              }
            ]}
          />
        )}
      </Flex>
    </Card>
  )
}
